import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Kpi, PageHeader, Panel } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { FormFields, compact, FORM_DIALOG } from "@/components/RecordDialog";
import { DetailGrid } from "@/components/PageHeader";

const STATUSES = ["Pending GST Check", "Review", "On Hold", "MSME Priority", "Approved", "Rejected"];

const NEW_FIELDS = [
  { section: "Invoice", name: "invoice_no", label: "Invoice #", required: true },
  { section: "Invoice", name: "invoice_date", label: "Invoice Date", type: "date", required: true },
  { section: "Invoice", name: "irn", label: "e-Invoice IRN" },
  { section: "Invoice", name: "po_ref", label: "Purchase Order", type: "ref", ref: "purchase_orders", ref_label: "code", required: true },
  { section: "Invoice", name: "payment_due_date", label: "Payment Due Date", type: "date" },
  { section: "Invoice", name: "gl_class", label: "GL Class", type: "select", options: ["Revenue", "Capital"] },
  { section: "Tax & amounts", name: "taxable_value", label: "Taxable Value (₹)", type: "currency" },
  { section: "Tax & amounts", name: "gst_rate", label: "GST Rate", type: "select", options: ["0%", "5%", "12%", "18%", "28%"] },
  { section: "Tax & amounts", name: "gst_amount", label: "GST Amount (₹)", type: "currency" },
  { section: "Tax & amounts", name: "tds_amount", label: "TDS (₹)", type: "currency" },
  { section: "Tax & amounts", name: "invoice_amount", label: "Total Invoice Amount (₹)", type: "currency", required: true },
  { section: "Tax & amounts", name: "remarks", label: "Remarks", type: "textarea" },
];

const GrnSelect = ({ poRef, value, onChange }) => {
  const [grns, setGrns] = useState([]);
  useEffect(() => {
    if (!poRef) { setGrns([]); return; }
    api.get("/grns").then((r) => setGrns(r.data.filter((g) => g.po_ref === poRef && g.status === "submitted")));
  }, [poRef]);
  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={!grns.length}>
      <SelectTrigger className="h-8 text-xs" data-testid="select-invoice-grn"><SelectValue placeholder={poRef ? (grns.length ? "Select GRN" : "No submitted GRN for this PO") : "Pick a PO first"} /></SelectTrigger>
      <SelectContent>{grns.map((g) => <SelectItem key={g.id} value={g.grn_no}>{g.grn_no} · {g.vendor}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const NewInvoiceDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ gl_class: "Revenue" }); }, [open]);
  const save = async () => {
    try {
      const body = { ...compact(f), status: "Review" };
      const { data } = await api.post("/invoices", body);
      toast.success(`${data.invoice_no} added · 3-way match ${data.po_match}`);
      onCreated();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-new-invoice" className={FORM_DIALOG}>
        <DialogHeader><DialogTitle className="font-heading">Add invoice</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Vendor, PO value, GRN accepted value, MSME and GST status are pulled automatically from Purchase Orders, GRNs, Vendor Master and GST Compliance.</p>
        <FormFields fields={NEW_FIELDS} form={f} setForm={setF} />
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5"><Label>GRN (submitted, for selected PO)</Label><GrnSelect poRef={f.po_ref} value={f.grn_ref} onChange={(v) => setF((s) => ({ ...s, grn_ref: v }))} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="btn-create-invoice">Add to queue</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReviewDialog = ({ inv, onClose, onSaved, editable }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (inv) setF({ status: inv.status, gl_class: inv.gl_class, gst_compliant: inv.gst_compliant, remarks: inv.remarks || "" }); }, [inv]);
  if (!inv) return null;
  const save = async () => {
    try { await api.put(`/invoices/${inv.id}`, f); toast.success(`${inv.invoice_no} updated`); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const row = (l, v, ok) => (
    <div className="flex items-center justify-between border-b border-slate-100 py-2 text-sm"><span className="text-slate-500">{l}</span><span className={`font-mono ${ok === false ? "text-rose-600" : ""}`}>{v}</span></div>
  );
  const near = (a) => Math.abs(a - inv.invoice_amount) <= inv.invoice_amount * 0.005;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialog-review-invoice" className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{inv.invoice_no} · {inv.vendor}</DialogTitle></DialogHeader>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">3-way match</p>
          {row(`PO ${inv.po_ref}`, inr(inv.po_amount), near(inv.po_amount))}
          {row(`GRN ${inv.grn_ref}`, inr(inv.grn_amount), near(inv.grn_amount))}
          {row("Invoice", inr(inv.invoice_amount))}
          <div className="mt-3"><DetailGrid testId="invoice-details" items={[["Invoice date", fmtDate(inv.invoice_date)], ["IRN", inv.irn], ["Due date", inv.payment_due_date && fmtDate(inv.payment_due_date)], ["Taxable", inv.taxable_value != null ? inr(inv.taxable_value) : ""], ["GST", inv.gst_amount != null ? `${inr(inv.gst_amount)} ${inv.gst_rate || ""}` : ""], ["TDS", inv.tds_amount != null ? inr(inv.tds_amount) : ""]]} /></div>
          <div className="mt-2 flex gap-2"><StatusBadge value={inv.po_match} testId="review-po-match" />{inv.msme && <StatusBadge value="MSME" />}<span className="text-xs text-slate-500">Ageing {inv.ageing} days</span></div>
        </div>
        {editable ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}><SelectTrigger data-testid="select-invoice-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>GL class</Label>
              <Select value={f.gl_class} onValueChange={(v) => setF((s) => ({ ...s, gl_class: v }))}><SelectTrigger data-testid="select-gl-class"><SelectValue /></SelectTrigger>
                <SelectContent>{["Revenue", "Capital"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch checked={f.gst_compliant} onCheckedChange={(v) => setF((s) => ({ ...s, gst_compliant: v }))} data-testid="switch-gst-compliant" />Vendor GST compliant</label>
            <div className="space-y-1.5 sm:col-span-2"><Label>Remarks</Label><Textarea rows={2} value={f.remarks} onChange={(e) => setF((s) => ({ ...s, remarks: e.target.value }))} data-testid="input-invoice-remarks" /></div>
          </div>
        ) : <p className="text-sm text-slate-500">Status: {inv.status}</p>}
        {editable && <DialogFooter><Button onClick={save} data-testid="btn-save-invoice">Save</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};

export default function InvoiceQueue() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("pending");
  const [review, setReview] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/invoices").then((r) => setRows(r.data));
    api.get("/invoices/stats").then((r) => setStats(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const filtered = useMemo(() => rows.filter((r) =>
    (status === "all" || (status === "pending" ? !["Approved", "Rejected"].includes(r.status) : r.status === status)) &&
    (!q || `${r.invoice_no} ${r.vendor}`.toLowerCase().includes(q.toLowerCase()))), [rows, q, status]);
  const editable = canWrite(user, "finance");

  return (
    <div>
      <PageHeader dept="Finance" title="Invoice & 3-Way Match Queue" subtitle="PO vs GRN vs invoice matching with MSME and GST compliance checks">
        {editable && <Button onClick={() => setNewOpen(true)} data-testid="btn-new-invoice"><Plus className="mr-1.5 h-4 w-4" />Add invoice</Button>}
      </PageHeader>
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi testId="kpi-total-pending" color={0} label="Total pending" value={stats.total_pending} sub={inr(stats.pending_value)} />
          <Kpi testId="kpi-overdue-30" label="Overdue > 30 days" value={stats.overdue_30} color={4} />
          <Kpi testId="kpi-msme-hold" label="On MSME hold" value={stats.msme_hold} color={3} />
          <Kpi testId="kpi-gst-noncompliant" label="GST non-compliant vendors" value={stats.gst_non_compliant_vendors} color={2} />
        </div>
      )}
      <Panel title={`Invoice queue · ${filtered.length}`} testId="invoice-table">
        <div className="flex flex-col gap-2 border-b border-slate-200 p-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input className="pl-8" placeholder="Search Invoice # or vendor" value={q} onChange={(e) => setQ(e.target.value)} data-testid="input-search-invoice" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="sm:w-52" data-testid="filter-invoice-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">All pending</SelectItem><SelectItem value="all">All invoices</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              {["Invoice #", "Vendor", "Amount", "PO Match", "GL Class", "Ageing", "Status", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} className="row-hover" data-testid={`row-invoice-${r.invoice_no}`}>
                  <TableCell className="font-mono text-xs font-medium">{r.invoice_no}<p className="font-sans text-[11px] text-slate-400">{fmtDate(r.invoice_date)}</p></TableCell>
                  <TableCell className="text-sm">{r.vendor}{r.msme && <span className="ml-1.5 rounded bg-indigo-50 px-1 text-[10px] text-indigo-700">MSME</span>}{!r.gst_compliant && <span className="ml-1 rounded bg-rose-50 px-1 text-[10px] text-rose-700">GST</span>}</TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-sm">{inr(r.invoice_amount)}</TableCell>
                  <TableCell><StatusBadge value={r.po_match} /></TableCell>
                  <TableCell className="text-sm">{r.gl_class}</TableCell>
                  <TableCell className={`whitespace-nowrap text-sm ${r.ageing > 30 ? "font-semibold text-rose-600" : ""}`}>{r.ageing} days</TableCell>
                  <TableCell><StatusBadge value={r.status} /></TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => setReview(r)} data-testid={`btn-review-${r.invoice_no}`}>Review<ArrowRight className="ml-1 h-3.5 w-3.5" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Panel>
      <ReviewDialog inv={review} editable={editable} onClose={() => setReview(null)} onSaved={load} />
      <NewInvoiceDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
    </div>
  );
}
